/* eslint-disable no-restricted-syntax */
import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useViewerControl } from 'libs/rendering/gltf/hooks';
import { GoPlus } from 'react-icons/go';
import { GrFormSubtract } from 'react-icons/gr';
import { Button, Box, SvgIcon } from 'components';
import { BsArrowLeftRight } from 'react-icons/bs';
import { insertDivider } from 'utils/jsxUtils';
import {
  getSurfaceLabel,
  getSurfaceIcon,
  getSurfaceParametersLabel,
} from 'apps/rfq/dfmConfig';

import {
  DiffStatus,
  getDiffStatusSymbol,
  getDiffStatusColor,
  getStrokeColor,
  getDiffStatusDarkColor,
  BodyDiff,
  MeshDiff,
  AssemblyDiff,
} from 'libs/rendering/gltf/diffConfig';

import {
  DiffViewportWrapper,
  ContentWrapper,
  GroupWrapper,
  SummaryWrapper,
  SummaryLabel,
  ChangesetWrapper,
  GroupLabel,
  TableWrapper,
  RowWrapper,
  RowSingleWrapper,
  FaceCell,
  IconCell,
  LabelCell,
  DataCell,
  SummaryRow,
  SummaryCell,
  PhysRowWrapper,
  PhysLabelCell,
} from './diffPanel.styled';

import { StatusBox } from './StatusBox';

const DIFF_GROUP_NAME = 'Changeset';
const DIFF_ASSY_ADD_NAME = 'Added';
const DIFF_ASSY_DEL_NAME = 'Deleted';
const DIFF_ASSY_TR_NAME = 'Transformed';
const DEFAULT_EXPANDED = false;

const iconSize = 12;
const iconAndTextGap = 8;

const diffRenderMap = {
  [DiffStatus.delete]: {
    className: 'border-wfRed',
    prefix: <GrFormSubtract size={iconSize} className=' font-helvetica mr-1' />,
  },
  [DiffStatus.edit]: {
    className: 'border-wfYellow',
    prefix: (
      <BsArrowLeftRight size={iconSize} className=' font-helvetica  mr-1' />
    ),
  },
  [DiffStatus.add]: {
    className: 'border-wfGreen',
    prefix: <GoPlus size={iconSize} className=' font-helvetica mr-1' />,
  },
  [DiffStatus.mergeSplit]: {
    className: 'border-wfPurple',
  },
};

const FaceItemWrapper = ({ children }) => {
  return (
    <div className=' font-helvetica pl-4 py-1 w-full flex items-center hover:bg-wfGrey100'>
      {children}
    </div>
  );
};
const maxSize = 12;

const DiffAnalysis = observer(function _DiffAnalysis() {
  const viewerControl = useViewerControl();
  const viewer = viewerControl?.viewer;
  const { diffTool, isDiffing } = viewer || {};

  const [diffGroupExpandState, SetDiffGroupExpandState] = useState(
    () => new Map(),
  );

  const camera = viewer?.settings?.CurrentCamera();

  const { isAssemblyDiff, isMeshDiff, hasResult } = diffTool || {};

  // summary metrics
  const result = diffTool?.GetResult();
  const addCount = result?.added.size();
  const delCount = result?.deleted.size();
  const editCount = diffTool?.editCount;
  const splitAddCount = diffTool?.splitAddCount;
  const mergeDeleteCount = diffTool?.mergeDeleteCount;

  let changeCount = addCount + delCount + splitAddCount + mergeDeleteCount;
  if (isAssemblyDiff || diffTool?.highlightEdit) {
    changeCount += editCount;
  }

  /* main diff data */
  const diffResult = !isAssemblyDiff
    ? isMeshDiff
      ? new MeshDiff(diffTool)
      : new BodyDiff(diffTool)
    : new AssemblyDiff(diffTool);

  /*
    actions
  */

  const ToggleDiffGroup = groupIx => {
    if (diffGroupExpandState.has(groupIx)) {
      diffGroupExpandState.delete(groupIx);
      SetDiffGroupExpandState(new Map(diffGroupExpandState)); // expand
    } else {
      SetDiffGroupExpandState(new Map(diffGroupExpandState.set(groupIx, true))); // collapse
    }
  };

  const ToggleDiffGroups = () => {
    if (!isAssemblyDiff) {
      for (let i = 0; i < diffResult?.groupCount; i++) {
        ToggleDiffGroup(diffResult?.groups[i].selfIx);
      }
    } else {
      ToggleDiffGroup(DIFF_ASSY_ADD_NAME);
      ToggleDiffGroup(DIFF_ASSY_DEL_NAME);
      ToggleDiffGroup(DIFF_ASSY_TR_NAME);
    }
  };

  /*
    effects
  */

  useEffect(() => {
    if (!DEFAULT_EXPANDED) {
      // by default all expanded, so collapse all
      ToggleDiffGroups();
    }
  }, [changeCount]);

  /*
    render
  */

  if (!isDiffing) return null;

  const getPhysData = () => {
    if (!diffTool || !result) return null;

    const refArea = diffTool.GetRefBody()?.GetSurface();
    const diffArea = diffTool.GetDiffBody()?.GetSurface();
    const areaChange = refArea > 0 && diffArea > 0 ? diffArea - refArea : 0;

    const refVol = diffTool.GetRefBody()?.GetVolume();
    const diffVol = diffTool.GetDiffBody()?.GetVolume();
    const volChange = refVol > 0 && diffVol > 0 ? diffVol - refVol : 0;

    return {
      refArea,
      diffArea,
      areaChange,
      areaPerc: areaChange ? 100.0 * (areaChange / refArea) : 0,
      refVol,
      diffVol,
      volChange,
      volPerc: volChange ? 100.0 * (volChange / refVol) : 0,
    };
  };

  const getDisplayDiffObject = () => {
    if (isMeshDiff) {
      const diffRes = diffResult.result;
      return {
        deletedCount: diffRes.deleted,
        editedCount: Math.max(diffRes.refPtCount, diffRes.diffPtCount),
        addedCount: diffRes.added,
      };
    }

    return {
      deletedCount: delCount,
      editedCount: editCount,
      addedCount: addCount,
    };
  };

  const getSizeSummary = () => {
    const { deletedCount, editedCount, addedCount } = getDisplayDiffObject();
    if (!diffTool || !result) return null;

    if (!isAssemblyDiff) {
      const physData = getPhysData();
      if (!physData) return null;

      const areaCount = physData.areaPerc.toFixed(1);
      const volumeCount = physData.volPerc.toFixed(1);

      const maxLength = String(
        Math.max(deletedCount, editedCount, addedCount),
      ).length;

      const unit = 12;
      const iconMarginRight = 4;

      const boxStyle = {
        width: Math.min(maxSize, maxLength) * unit + iconSize + iconMarginRight,
      };

      return (
        <div
          data-testid='CADPage-DiffSummary'
          className=' overscroll-contain font-helvetica flex bg-wfWhite border-b border-wfGrey100 p-4 items-stretch'
        >
          <div className=' font-helvetica w-1/2'>
            {!!deletedCount && (
              <PhysRowWrapper>
                <PhysLabelCell header>Deleted</PhysLabelCell>
                <StatusBox
                  style={boxStyle}
                  maxSize={maxSize}
                  prefix={diffRenderMap[DiffStatus.delete].prefix}
                  className={diffRenderMap[DiffStatus.delete].className}
                  value={String(deletedCount)}
                />
              </PhysRowWrapper>
            )}
            {!!editedCount && (
              <PhysRowWrapper>
                <PhysLabelCell header>Edited</PhysLabelCell>

                <StatusBox
                  style={boxStyle}
                  maxSize={maxSize}
                  className={diffRenderMap[DiffStatus.edit].className}
                  prefix={diffRenderMap[DiffStatus.edit].prefix}
                  value={String(editedCount)}
                />
              </PhysRowWrapper>
            )}
            {!!addCount && (
              <PhysRowWrapper>
                <PhysLabelCell header>Added</PhysLabelCell>
                <StatusBox
                  style={boxStyle}
                  maxSize={maxSize}
                  className={diffRenderMap[DiffStatus.add].className}
                  prefix={diffRenderMap[DiffStatus.add].prefix}
                  value={String(addedCount)}
                />
              </PhysRowWrapper>
            )}

            {Number(areaCount) !== 0 && (
              <PhysRowWrapper>
                <PhysLabelCell header>Area</PhysLabelCell>
                <PhysLabelCell change={areaCount}>
                  {areaCount > 0 && '+'}
                  {`${areaCount} %`}
                </PhysLabelCell>
              </PhysRowWrapper>
            )}

            {Number(volumeCount) !== 0 && (
              <PhysRowWrapper>
                <PhysLabelCell header>Volume</PhysLabelCell>
                <PhysLabelCell change={volumeCount}>
                  {volumeCount > 0 && '+'}
                  {`${volumeCount} %`}
                </PhysLabelCell>
              </PhysRowWrapper>
            )}
          </div>
        </div>
      );
    }

    // TODO: assmebly
    return null;
  };

  /*
    changeset
  */

  const getSummary = (
    add,
    del,
    edit,
    splitAdd,
    mergeDelete,
    strong,
    atRight,
  ) => {
    const delTotal = diffTool.highlightMergeSplit ? del : del + mergeDelete;
    const addTotal = diffTool.highlightMergeSplit ? add : add + splitAdd;
    return (
      <SummaryRow atRight={atRight}>
        {insertDivider(
          [
            delTotal > 0 && (
              <SummaryCell
                key={DiffStatus.delete}
                status={DiffStatus.delete}
                border={getDiffStatusDarkColor(DiffStatus.delete)}
                strong={strong}
              >{`${DiffStatus.delete} ${delTotal}`}</SummaryCell>
            ),
            addTotal > 0 && (
              <SummaryCell
                status={DiffStatus.add}
                key={DiffStatus.add}
                border={getDiffStatusDarkColor(DiffStatus.add)}
                strong={strong}
              >{`${DiffStatus.add} ${addTotal}`}</SummaryCell>
            ),
            (diffTool.isAssemblyDiff || diffTool.highlightEdit) && edit > 0 && (
              <SummaryCell
                status={DiffStatus.edit}
                key={DiffStatus.edit}
                border={getDiffStatusDarkColor(DiffStatus.edit)}
                strong={strong}
              >{`${DiffStatus.edit} ${edit}`}</SummaryCell>
            ),
            diffTool.highlightMergeSplit && splitAdd + mergeDelete > 0 && (
              <SummaryCell
                status={DiffStatus.mergeSplit}
                key={DiffStatus.mergeSplit}
                border={getDiffStatusDarkColor(DiffStatus.mergeSplit)}
                strong={strong}
              >
                {`- ${mergeDelete}`}{' '}
                <div className=' font-helvetica mx-2'>|</div>
                {`+ ${splitAdd}`}
              </SummaryCell>
            ),
          ],
          { divider: <div className=' font-helvetica  mx-1' /> },
        )}
      </SummaryRow>
    );
  };

  const getTableCell = (
    status,
    faceJson,
    isMerge,
    isSubGroupEnd,
    isRef,
    selIx,
    isSingle = false,
  ) => {
    const surfEnum = viewerControl?.viewer?.module.SurfaceType;

    const label = getSurfaceLabel(faceJson?.type, surfEnum);
    const icon = getSurfaceIcon(faceJson?.type, surfEnum);
    const parsLables =
      status === DiffStatus.edit ? getSurfaceParametersLabel(faceJson) : null;

    const clr = getDiffStatusColor(status);
    const darkClr = getDiffStatusDarkColor(status);

    let symbol = getDiffStatusSymbol(status);
    if (status === DiffStatus.mergeSplit) {
      symbol = isMerge ? '⋺' : '⋲';
    }

    let hover = null;
    if (selIx >= 0 && status) {
      hover = () => {
        viewer._wasm.AddDiffHighlight(selIx, isRef);
      };
    }

    return (
      <FaceCell
        status={status}
        color={clr}
        border={darkClr}
        isSingle={isSingle}
        isSubGroupEnd={isSubGroupEnd}
        onMouseEnter={hover}
        onMouseLeave={() => viewer.selectionManager.ClearHighlight()}
      >
        <FaceItemWrapper>
          <IconCell stroke={getStrokeColor(status)}>
            <SvgIcon icon={icon} size='medium' />
          </IconCell>
          <LabelCell>{label}</LabelCell>
          {parsLables && <DataCell>{parsLables}</DataCell>}
        </FaceItemWrapper>
      </FaceCell>
    );
  };

  const getTableRow = (
    groupIx,
    rowIx,
    status,
    refFace,
    diffFace,
    isMerge,
    isSubGroupEnd,
    isSingle = false,
  ) => {
    let cell = null;
    let cell2 = null;

    if (refFace) {
      const publicStatus =
        status !== DiffStatus.mergeSplit || diffTool.highlightMergeSplit
          ? status
          : DiffStatus.delete;

      const faceJson = JSON.parse(refFace.GetSurfaceData());
      cell = getTableCell(
        publicStatus,
        faceJson,
        isMerge,
        isSubGroupEnd,
        true, // isRef
        refFace.selfIx,
        isSingle,
      );
    } else {
      cell = getTableCell(null, null, false, false, true, -1, isSingle);
    }

    if (diffFace) {
      const publicStatus =
        status !== DiffStatus.mergeSplit || diffTool.highlightMergeSplit
          ? status
          : DiffStatus.add;

      const faceJson = JSON.parse(diffFace.GetSurfaceData());
      cell2 = getTableCell(
        publicStatus,
        faceJson,
        isMerge,
        isSubGroupEnd,
        false, // is diff
        diffFace.selfIx,
        isSingle,
      );
    } else {
      cell2 = getTableCell(null, null, false, false, false, -1, isSingle);
    }

    if (isSingle) {
      return (
        <RowSingleWrapper key={`diff_${groupIx}_${rowIx}`}>
          {cell || cell2}
        </RowSingleWrapper>
      );
    }

    return (
      <RowWrapper key={`diff_${groupIx}_${rowIx}`}>
        {cell}
        {cell2}
      </RowWrapper>
    );
  };

  const getGroupRows = (ix, diffGroup) => {
    if (!diffGroup) return null;
    if (isAssemblyDiff) return null;

    const refBody = diffTool.GetRefBody();
    const refWasmObj = refBody?.isFaceted
      ? refBody.GetBodyMesh()?.GetMeshFrame()
      : refBody.GetBrep();

    const diffBody = diffTool.GetDiffBody();
    const diffWasmObj = diffBody?.isFaceted
      ? diffBody.GetBodyMesh()?.GetMeshFrame()
      : diffBody.GetBrep();

    const rows = [];
    const status =
      diffGroup.seeds.size !== diffGroup.seedsRev.size
        ? DiffStatus.mergeSplit
        : DiffStatus.edit;

    // merge / split group
    const nSeeds = diffGroup.seeds.size;
    const nSeedsRev = diffGroup.seedsRev.size;
    const isMerge = nSeeds > nSeedsRev;
    let checkCount = 0;
    let count = 0;

    if (nSeeds >= nSeedsRev) {
      for (const key of diffGroup.seeds.keys()) {
        let diffFace = null;
        const face = refWasmObj.GetFace(key);
        count += 1;

        if (checkCount < nSeedsRev) {
          const diffKey = diffGroup.seeds.get(key);
          if (diffGroup.seedsRev.has(diffKey)) {
            diffFace = diffWasmObj.GetFace(diffKey);
            checkCount += 1;
          }
        }

        rows.push(
          getTableRow(
            ix,
            rows.length,
            status,
            face,
            diffFace,
            isMerge,
            count === nSeeds,
            false,
          ),
        );
      }
    } else {
      for (const key of diffGroup.seedsRev.keys()) {
        let refFace = null;
        const face = diffWasmObj.GetFace(key);
        count += 1;

        if (checkCount < nSeeds) {
          const refKey = diffGroup.seedsRev.get(key);
          if (diffGroup.seeds.has(refKey)) {
            refFace = refWasmObj.GetFace(refKey);
            checkCount += 1;
          }
        }

        rows.push(
          getTableRow(
            ix,
            rows.length,
            status,
            refFace,
            face,
            isMerge,
            count === nSeedsRev,
            false,
          ),
        );
      }
    }

    // edits
    if (diffTool.highlightEdit) {
      for (const key of diffGroup.edits.keys()) {
        const face = refWasmObj.GetFace(key);

        const diffKey = diffGroup.edits.get(key);
        const diffFace = diffWasmObj.GetFace(diffKey);

        rows.push(
          getTableRow(ix, rows.length, DiffStatus.edit, face, diffFace, false),
        );
      }
    }

    // changes
    const wasmObj = diffGroup.isAdd ? diffWasmObj : refWasmObj;
    const n = diffGroup.changes.length;
    for (let i = 0; i < n; i++) {
      const nRows = diffGroup.changes[i].length;
      for (let j = 0; j < nRows; j++) {
        const isSubGroupEnd = j === nRows - 1;
        const isGroupEnd = isSubGroupEnd && i === n - 1;

        const faceIx = diffGroup.changes[i][j];
        const face = wasmObj?.GetFace(faceIx);
        if (diffGroup.isAdd) {
          rows.push(
            getTableRow(
              ix,
              rows.length,
              DiffStatus.add,
              null,
              face,
              false,
              !isGroupEnd && isSubGroupEnd,
              true,
            ),
          );
        } else {
          rows.push(
            getTableRow(
              ix,
              rows.length,
              DiffStatus.delete,
              face,
              null,
              false,
              !isGroupEnd && isSubGroupEnd,
              true,
            ),
          );
        }
      }
    }

    return rows;
  };

  // DIFF
  const getDiffCell = (status, instance, isRef, selIx, instance2) => {
    const label = instance?.GetBody()?.name;
    const selfTarget = instance?.GetSelfTargetItem();
    const selfTarget2 = instance2?.GetSelfTargetItem();

    const clr = getDiffStatusColor(status);
    const darkClr = getDiffStatusDarkColor(status);

    let hover = null;
    if (selIx >= 0 && status) {
      hover = () => {
        if (selfTarget) {
          // tell selection is from UI instance tree node...
          selfTarget.source = viewer?.module.SelectionSource.AdvancedGui;
          selfTarget.cadFileId = isRef ? diffTool.refId : diffTool.diffId;
          viewer.selectionManager.AddHighlight(selfTarget._wasm);

          if (instance2) {
            selfTarget2.source = viewer?.module.SelectionSource.AdvancedGui;
            selfTarget2.cadFileId = diffTool.diffId;
            viewer.selectionManager.AddHighlight(selfTarget2._wasm);
          }
        }
      };
    }

    return (
      <FaceCell
        status={status}
        color={clr}
        border={darkClr}
        isSubGroupEnd={false}
        onMouseEnter={hover}
        onMouseLeave={() => viewer.selectionManager.ClearHighlight()}
      >
        <FaceItemWrapper>
          <LabelCell className='ml-2'>{label}</LabelCell>
        </FaceItemWrapper>
      </FaceCell>
    );
  };

  const getDiffRow = (label, rowIx, status, refInstance, diffInstance) => {
    let cell = null;

    if (refInstance) {
      cell = getDiffCell(
        status,
        refInstance,
        true, // isRef
        refInstance.selfIx,
        diffInstance,
      );
    } else if (diffInstance) {
      cell = getDiffCell(
        status,
        diffInstance,
        false, // is diff
        diffInstance.selfIx,
      );
    }

    return (
      <RowSingleWrapper key={`diff_${label}_${rowIx}`}>{cell}</RowSingleWrapper>
    );
  };

  const getDiffSummary = (label, status, count, isFirst) => {
    const expanded = !diffGroupExpandState.has(label);

    let list = null;
    if (status === DiffStatus.add) list = diffResult?.added;
    if (status === DiffStatus.delete) list = diffResult?.deleted;
    if (status === DiffStatus.edit) list = [...diffResult?.transformed.keys()];

    const refWasmObj = diffTool.GetRefAssembly();
    const diffWasmObj = diffTool.GetDiffAssembly();
    const wasmObj = status === DiffStatus.delete ? refWasmObj : diffWasmObj;

    const n = list.length;
    const rows = [];
    for (let i = 0; i < n; i++) {
      const refId = list[i];
      if (status !== DiffStatus.edit) {
        const instance = wasmObj?.GetInstanceById(refId);
        rows.push(
          getDiffRow(
            label,
            i,
            status,
            status === DiffStatus.delete ? instance : null,
            status === DiffStatus.delete ? null : instance,
          ),
        );
      } else {
        const instance = refWasmObj?.GetInstanceById(refId);
        const diffId = diffResult?.transformed.get(refId);
        const instanceDiff = diffWasmObj?.GetInstanceById(diffId);
        rows.push(getDiffRow(label, i, status, instance, instanceDiff));
      }
    }

    return (
      <GroupWrapper key={`group_${label}`}>
        <ChangesetWrapper
          isFirst={isFirst}
          expanded={expanded}
          // </GroupWrapper>onMouseEnter={() =>
          // viewer._wasm.AddDiffGroupHighlight(group.selfIx)
          // }
          onMouseLeave={() => viewer.selectionManager.ClearHighlight()}
          onClick={() => ToggleDiffGroup(label)}
        >
          <Button
            size='xsmall'
            appearance='plain'
            variant='minimal'
            icon={expanded ? 'caret_down' : 'caret_right'}
            style={{ marginRight: iconAndTextGap }}
          />
          <GroupLabel
            style={{
              fontWeight: expanded ? 800 : 400,
              fontSize: '0.875rem',
            }}
          >
            {label}
          </GroupLabel>
          <SummaryRow atRight={true}>
            <SummaryCell
              status={status}
              border={getDiffStatusDarkColor(status)}
            >{`${status} ${count}`}</SummaryCell>
          </SummaryRow>
        </ChangesetWrapper>
        <TableWrapper expanded={expanded}>{rows}</TableWrapper>
      </GroupWrapper>
    );
  };

  if (!camera) return null;
  if (!isDiffing) return null;
  if (!diffTool) return null;
  if (!hasResult) {
    return (
      <DiffViewportWrapper>
        <Box sx={{ margin: '1rem' }}>Diffing files, please wait...</Box>
      </DiffViewportWrapper>
    );
  }

  if (isMeshDiff) {
    return getSizeSummary();
  }

  if (isAssemblyDiff) {
    return (
      <DiffViewportWrapper>
        {/*  change */}
        {getSizeSummary()}
        {/* summary */}
        <SummaryWrapper className=' font-helvetica w-full px-4 py-2'>
          <SummaryLabel>{`${changeCount} changed instance${
            changeCount > 1 ? 's' : ''
          }`}</SummaryLabel>

          {getSummary(
            addCount,
            delCount,
            editCount,
            splitAddCount,
            mergeDeleteCount,
            true,
            true,
          )}
        </SummaryWrapper>
        {/* changes */}
        <ContentWrapper>
          {insertDivider(
            [
              delCount > 0 &&
                getDiffSummary(
                  DIFF_ASSY_DEL_NAME,
                  DiffStatus.delete,
                  delCount,
                  true,
                ),
              addCount > 0 &&
                getDiffSummary(
                  DIFF_ASSY_ADD_NAME,
                  DiffStatus.add,
                  addCount,
                  delCount === 0,
                ),
              editCount > 0 &&
                getDiffSummary(
                  DIFF_ASSY_TR_NAME,
                  DiffStatus.edit,
                  editCount,
                  delCount === 0 && addCount === 0,
                ),
            ],
            {
              divider: (
                <div className=' font-helvetica border-b border-wfGrey100' />
              ),
            },
          )}
        </ContentWrapper>
      </DiffViewportWrapper>
    );
  }

  return (
    <DiffViewportWrapper>
      {/*  change */}
      {getSizeSummary()}

      {/* summary */}
      <SummaryWrapper className=' font-helvetica w-full px-4 py-2'>
        <SummaryLabel>
          <p className=' font-helvetica font-semibold'>
            {`${diffResult?.groupCount} ${DIFF_GROUP_NAME}${
              diffResult?.groupCount > 1 ? 's' : ''
            }`}
          </p>
          <p className=' font-helvetica ml-1 text-wfGrey'>{`${changeCount} Changed face${
            changeCount > 1 ? 's' : ''
          }`}</p>
        </SummaryLabel>

        {getSummary(
          addCount,
          delCount,
          editCount,
          splitAddCount,
          mergeDeleteCount,
          true,
          true,
        )}
      </SummaryWrapper>

      {/* changes */}
      <ContentWrapper>
        {diffResult?.groups?.map((group, i) => {
          const groupEls = getGroupRows(i, group);
          const expanded = !diffGroupExpandState.has(group.selfIx);
          return (
            <GroupWrapper key={`group_${group.selfIx}`}>
              <ChangesetWrapper
                isFirst={i === 0}
                expanded={expanded}
                onMouseEnter={() =>
                  viewer._wasm.AddDiffGroupHighlight(group.selfIx)
                }
                onMouseLeave={() => viewer.selectionManager.ClearHighlight()}
                onClick={() => ToggleDiffGroup(group.selfIx)}
              >
                <Button
                  size='xsmall'
                  appearance='plain'
                  variant='minimal'
                  icon={expanded ? 'caret_down' : 'caret_right'}
                  style={{ marginRight: iconAndTextGap }}
                />
                <GroupLabel
                  className={`text-sm ${expanded ? 'font-extrabold' : ''}`}
                >{`${DIFF_GROUP_NAME} ${i + 1}`}</GroupLabel>
                {getSummary(
                  group.addCount,
                  group.delCount,
                  group.editCount,
                  group.splitAddCount,
                  group.mergeDeleteCount,
                  true,
                  true,
                )}
              </ChangesetWrapper>
              <TableWrapper expanded={expanded}>
                {groupEls.map(rowEl => {
                  return rowEl;
                })}
              </TableWrapper>
            </GroupWrapper>
          );
        })}
      </ContentWrapper>
    </DiffViewportWrapper>
  );
});

export default DiffAnalysis;
